/**
 * Resource-graph PNG export helpers.
 * The chart axis is captured as shown. Weekly buckets stay Monday–Sunday
 * (Sunday week-ending); this module does not rebucket dates.
 */
import dayjs from './date.js';

export function fileSlug (value, fallback) {
	const slug = String(value == null ? '' : value)
		.replace(/\.(xer|xml|mpp)$/i, '')
		.replace(/[^A-Za-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, 60);
	return slug || fallback;
}

export function resourceGraphImageFilename ({ project, dataset, date } = {}) {
	const day = /^\d{4}-\d{2}-\d{2}$/.test(String(date || ''))
		? String(date)
		: dayjs().format('YYYY-MM-DD');
	return `resource-graph_${fileSlug(project, 'project')}_${fileSlug(dataset, 'dataset')}_${day}.png`;
}

/** Crisp export: twice the device pixel ratio (at least 2). */
export function resourceGraphExportScale (devicePixelRatio) {
	const dpr = Number(devicePixelRatio);
	const safe = dpr > 0 ? dpr : 1;
	return 2 * safe;
}

export function resourceGraphExportTitleLines ({
	projectName,
	datasetId,
	filterSummary,
	scaleLabel,
	rsrcType,
	unitLabel,
	rangeStart,
	rangeEnd,
	earnNote,
	nameNote
} = {}) {
	const lines = [projectName || 'Project'];
	lines.push(`Dataset ${datasetId == null || datasetId === '' ? '—' : datasetId}`);
	if (rangeStart && rangeEnd && rangeStart !== rangeEnd) {
		lines.push(`${rangeStart} – ${rangeEnd}`);
	} else if (rangeStart || rangeEnd) {
		lines.push(rangeStart || rangeEnd);
	}
	const meta = [rsrcType, scaleLabel, unitLabel, earnNote, nameNote, filterSummary]
		.map(part => (part == null ? '' : String(part).trim()))
		.filter(Boolean);
	if (meta.length) {
		lines.push(meta.join(' · '));
	}
	return lines;
}

function wrapText (ctx, text, maxWidth) {
	const words = String(text || '').split(/\s+/).filter(Boolean);
	if (!words.length) {
		return [''];
	}
	const lines = [];
	let current = '';
	words.forEach(word => {
		const next = current ? `${current} ${word}` : word;
		if (current && ctx.measureText(next).width > maxWidth) {
			lines.push(current);
			current = word;
		} else {
			current = next;
		}
	});
	if (current) {
		lines.push(current);
	}
	return lines;
}

/**
 * Stack a white title band above the captured chart (legend included in chartCanvas).
 * fontSize is in canvas pixels so the title matches the chart's export scale.
 */
export function composeResourceGraphCanvas (chartCanvas, lines) {
	const width = chartCanvas.width;
	const fontSize = Math.max(22, Math.round(width / 56));
	const lineHeight = Math.round(fontSize * 1.35);
	const padX = Math.round(fontSize * 0.9);
	const padY = Math.round(fontSize * 0.7);
	const maxWidth = Math.max(40, width - padX * 2);
	const ctxMeasure = chartCanvas.getContext('2d');
	const laidOut = [];
	(lines || []).forEach((line, index) => {
		ctxMeasure.font = `${index === 0 ? '700' : '400'} ${fontSize}px Arial, sans-serif`;
		wrapText(ctxMeasure, line, maxWidth).forEach(text => {
			laidOut.push({ text, bold: index === 0 });
		});
	});
	const titleHeight = padY * 2 + lineHeight * Math.max(1, laidOut.length);
	const out = document.createElement('canvas');
	out.width = width;
	out.height = chartCanvas.height + titleHeight;
	const ctx = out.getContext('2d');
	ctx.fillStyle = '#ffffff';
	ctx.fillRect(0, 0, out.width, out.height);
	ctx.textBaseline = 'top';
	ctx.fillStyle = '#222222';
	laidOut.forEach((row, index) => {
		ctx.font = `${row.bold ? '700' : '400'} ${fontSize}px Arial, sans-serif`;
		ctx.fillText(row.text, padX, padY + index * lineHeight);
	});
	ctx.strokeStyle = '#dddddd';
	ctx.lineWidth = Math.max(1, Math.round(fontSize / 16));
	ctx.beginPath();
	ctx.moveTo(0, titleHeight - 0.5);
	ctx.lineTo(width, titleHeight - 0.5);
	ctx.stroke();
	ctx.drawImage(chartCanvas, 0, titleHeight);
	return out;
}

export function downloadCanvasPng (canvas, filename) {
	return new Promise((resolve, reject) => {
		const finish = href => {
			const link = document.createElement('a');
			link.href = href;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			if (String(href).indexOf('blob:') === 0) {
				URL.revokeObjectURL(href);
			}
			resolve();
		};
		if (typeof canvas.toBlob === 'function') {
			canvas.toBlob(blob => {
				if (!blob) {
					try {
						finish(canvas.toDataURL('image/png'));
					} catch (err) {
						reject(err);
					}
					return;
				}
				finish(URL.createObjectURL(blob));
			}, 'image/png');
			return;
		}
		try {
			finish(canvas.toDataURL('image/png'));
		} catch (err) {
			reject(err);
		}
	});
}
